import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Zap, ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { key: "building", title: "What are you building?", type: "single", options: ["A SaaS product", "An AI tool", "A no-code app", "Still exploring ideas", "An agency / service"] },
  { key: "skills", title: "What are your skills?", type: "multi", options: ["Full-stack dev", "Frontend", "Backend", "No-code", "Design", "Marketing", "Sales", "AI/ML"] },
  { key: "industries", title: "Which industries interest you?", type: "multi", options: ["AI", "FinTech", "Healthcare", "Real Estate", "Restaurants", "Creator Economy", "Developer Tools", "E-commerce", "Field Service", "Education"] },
  { key: "technical_level", title: "How technical are you?", type: "single", options: ["Non-technical", "Somewhat technical", "Technical", "Very technical"] },
  { key: "business_model", title: "Preferred business model?", type: "single", options: ["Subscription / SaaS", "Marketplace", "Usage-based", "One-time", "Freemium", "Not sure yet"] },
  { key: "time_commitment", title: "How much time can you dedicate?", type: "single", options: ["Nights & weekends", "Part-time", "Full-time"] },
  { key: "revenue_goal", title: "What's your revenue goal?", type: "single", options: ["$1k MRR", "$10k MRR", "$50k MRR", "$100k+ MRR"] },
];

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const current = STEPS[step];
  const value = data[current.key];

  const toggle = (opt) => {
    if (current.type === "single") {
      setData({ ...data, [current.key]: opt });
    } else {
      const arr = data[current.key] || [];
      setData({ ...data, [current.key]: arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt] });
    }
  };

  const canNext = current.type === "single" ? !!value : (value || []).length > 0;

  const next = async () => {
    if (step < STEPS.length - 1) { setStep(step + 1); return; }
    setSaving(true);
    try {
      const payload = {
        building: data.building, skills: data.skills || [], industries: data.industries || [],
        technical_level: data.technical_level, business_model: data.business_model,
        time_commitment: data.time_commitment, revenue_goal: data.revenue_goal, technologies: [],
      };
      await api.post("/auth/onboarding", payload);
      await refresh();
      toast.success("Profile ready! Finding your opportunities…");
      navigate("/dashboard");
    } catch {
      toast.error("Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const isSelected = (opt) => current.type === "single" ? value === opt : (value || []).includes(opt);

  return (
    <div className="min-h-screen bg-[#0B0F19] grid-bg flex flex-col items-center justify-center px-4 py-12 relative">
      <div className="aurora" />
      <div className="relative z-10 w-full max-w-xl">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-indigo-500 flex items-center justify-center"><Zap className="w-4 h-4 text-slate-950" fill="currentColor" /></div>
          <span className="font-display font-extrabold text-slate-50">Build your Builder Profile</span>
        </div>

        <div className="h-1.5 rounded-full bg-white/8 mb-8 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-cyan-400 to-indigo-500 transition-all duration-500" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
        </div>

        <div className="glass rounded-2xl p-8 fade-up" key={step}>
          <p className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-2">Step {step + 1} / {STEPS.length}</p>
          <h2 className="font-display text-2xl font-bold text-slate-50 mb-6">{current.title}</h2>
          <div className="flex flex-wrap gap-2.5">
            {current.options.map((opt) => (
              <button key={opt} onClick={() => toggle(opt)} data-testid={`onboarding-opt-${opt.toLowerCase().replace(/[^a-z]+/g,'-')}`}
                className={`px-4 py-2.5 rounded-xl border text-sm transition-all ${isSelected(opt) ? "border-cyan-500 bg-cyan-500/15 text-cyan-200" : "border-white/10 text-slate-300 hover:border-white/25"}`}>
                {isSelected(opt) && <Check className="w-3.5 h-3.5 inline mr-1.5" />}{opt}
              </button>
            ))}
          </div>
          <div className="flex justify-between items-center mt-8">
            <button onClick={() => step > 0 && setStep(step - 1)} className={`text-sm text-slate-500 hover:text-slate-300 ${step === 0 ? "invisible" : ""}`}>Back</button>
            <Button onClick={next} disabled={!canNext || saving} data-testid="onboarding-next-btn"
              className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (step === STEPS.length - 1 ? "Finish" : "Continue")}
              {!saving && <ArrowRight className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
